import { useState } from 'react';
import { Search, Check, Plus, Phone, Mail, Globe, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { API, SECTEURS } from './constants';

export function CrmRecherche({ onImport }) {
  const [secteur, setSecteur] = useState('entreprise');
  const [zone, setZone] = useState('66');
  const [motcle, setMotcle] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [searchInfo, setSearchInfo] = useState(null);

  const doSearch = async () => {
    setSearching(true); setResults([]); setSelected(new Set()); setSearchInfo(null);
    try {
      const res = await fetch(`${API}/api/crm/search/osm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secteur, zone, motcle, limit: 50 }),
      });
      const data = await res.json();
      if (res.ok) {
        setResults(data.results || []);
        setSearchInfo({ total: data.total, source: data.source, zone: data.zone });
        if (data.results?.length === 0) toast.info('Aucun resultat \u2014 essayez un autre secteur ou zone');
      } else {
        toast.error(data.detail || 'Erreur recherche');
      }
    } catch { toast.error('Erreur reseau'); }
    setSearching(false);
  };

  const toggleSelect = (idx) => { setSelected(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; }); };
  const selectAll = () => { if (selected.size === results.length) setSelected(new Set()); else setSelected(new Set(results.map((_, i) => i))); };

  const handleImport = async () => {
    const toImport = results.filter((_, i) => selected.has(i)).map(r => ({ ...r, secteur }));
    if (toImport.length === 0) return;
    try {
      const res = await fetch(`${API}/api/crm/prospects/import`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prospects: toImport }) });
      const data = await res.json();
      if (data.imported > 0) toast.success(`${data.imported} prospect(s) importe(s)`);
      else toast.info('Tous les prospects existent deja');
      onImport(); setSelected(new Set());
    } catch { toast.error('Erreur import'); }
  };

  return (
    <div className="space-y-4" data-testid="crm-recherche">
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <div>
          <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1.5">Secteur d'activite</label>
          <div className="grid grid-cols-3 gap-1.5">
            {Object.entries(SECTEURS).map(([k, v]) => {
              const Icon = v.icon;
              return (
                <button key={k} onClick={() => setSecteur(k)} className={`text-[10px] py-2.5 px-1 rounded-lg border text-center transition-all flex flex-col items-center gap-1 ${secteur === k ? 'border-[#1B2A52] bg-[#1B2A52] text-white' : 'border-gray-200 text-gray-500'}`} data-testid={`search-secteur-${k}`}>
                  <Icon size={14} /><span>{v.label.split('/')[0].trim()}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-[10px] text-gray-400 uppercase block mb-1">Departement</label>
            <select value={zone} onChange={e => setZone(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-xs bg-white" data-testid="search-zone">
              <option value="66">66 \u2014 Pyrenees-Orientales</option>
              <option value="11">11 \u2014 Aude</option>
              <option value="34">34 \u2014 Herault</option>
            </select>
          </div>
          <div><label className="text-[10px] text-gray-400 uppercase block mb-1">Mot-cle (optionnel)</label>
            <Input value={motcle} onChange={e => setMotcle(e.target.value)} placeholder="garage, institut..." className="text-xs h-[38px]" onKeyDown={e => e.key === 'Enter' && doSearch()} />
          </div>
        </div>
        <Button onClick={doSearch} disabled={searching} className="w-full bg-[#1B2A52] hover:bg-[#243558] text-white" data-testid="search-btn">
          {searching ? <><Loader2 size={14} className="animate-spin mr-2" /> Recherche en cours...</> : <><Search size={14} className="mr-2" /> Rechercher sur OpenStreetMap</>}
        </Button>
        <p className="text-[10px] text-[#009893] text-center font-medium">Donnees reelles \u2014 OpenStreetMap (gratuit, illimite)</p>
      </div>

      {searchInfo && (
        <div className="flex items-center gap-2 text-[10px] text-gray-400">
          <span className="bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">{searchInfo.source}</span>
          <span>{searchInfo.total} resultat(s) \u2014 Dept. {searchInfo.zone}</span>
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><span className="text-xs text-gray-500 font-medium">{results.length} resultat{results.length > 1 ? 's' : ''}</span>
              <button onClick={selectAll} className="text-[10px] text-[#009893] hover:underline">{selected.size === results.length ? 'Deselectionner' : 'Tout selectionner'}</button>
            </div>
            {selected.size > 0 && <Button onClick={handleImport} size="sm" className="bg-[#009893] hover:bg-[#00807b] text-white text-xs" data-testid="import-btn"><Plus size={12} className="mr-1" /> Importer {selected.size}</Button>}
          </div>
          {results.map((r, i) => (
            <div key={i} onClick={() => toggleSelect(i)} className={`bg-white rounded-xl border p-3 cursor-pointer transition-all ${selected.has(i) ? 'border-[#009893] bg-[#009893]/5 ring-1 ring-[#009893]/20' : 'hover:shadow-sm'}`} data-testid={`result-${i}`}>
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${selected.has(i) ? 'border-[#009893] bg-[#009893]' : 'border-gray-200'}`}>{selected.has(i) && <Check size={10} className="text-white" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-gray-900">{r.nom}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 text-green-600 uppercase font-medium">OSM</span>
                    {r.osm_type && <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{r.osm_type}</span>}
                  </div>
                  {r.contact && <p className="text-xs text-gray-500">{r.contact}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400 flex-wrap">
                    {(r.ville || r.adresse) && <span className="flex items-center gap-0.5"><MapPin size={10} /> {[r.adresse, r.code_postal, r.ville].filter(Boolean).join(' ')}</span>}
                    {r.tel && <span className="flex items-center gap-0.5"><Phone size={10} /> {r.tel}</span>}
                    {r.email && <span className="flex items-center gap-0.5"><Mail size={10} /> {r.email}</span>}
                    {r.site_web && <a href={r.site_web} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center gap-0.5 text-blue-500 hover:underline"><Globe size={10} /> Site</a>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
